import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, Heart, Share2, BookHeart, Images, Info, Mic, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, slDate, guestIdentity, photosLabel, videosLabel, guestsLabel, shareLink } from "@/lib/api";
import Gallery from "@/components/guest/Gallery";
import Guestbook from "@/components/guest/Guestbook";
import UploadFlow from "@/components/guest/UploadFlow";
import VoiceRecorder from "@/components/guest/VoiceRecorder";
import { GuestTimeline, VoiceList } from "@/components/guest/GuestExtras";

const DEFAULT_COVER = "https://images.unsplash.com/photo-1606490194859-07c18c9f0968?w=1600&q=80&fm=jpg";

function useNoIndex() {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => m.remove();
  }, []);
}

function Notice({ title, text }) {
  return (
    <div className="min-h-screen grid place-items-center px-6 text-center" data-testid="guest-notice">
      <div className="space-y-4 max-w-md">
        <h1 className="font-display text-4xl">{title}</h1>
        <p className="text-[#6C6560]">{text}</p>
        <Link to="/" className="btn-line inline-block px-6 py-3 text-sm">Na domačo stran</Link>
      </div>
    </div>
  );
}

export default function GuestWedding() {
  const { slug } = useParams();
  const [w, setW] = useState(null);
  const [err, setErr] = useState(false);
  const [tab, setTab] = useState("gallery");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const token = guestIdentity(slug);
  useNoIndex();

  const load = () => api.get(`/public/w/${slug}`).then((r) => { setW(r.data); document.title = `${r.data.title} · Poročna galerija`; }).catch(() => setErr(true));
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (err) return <Notice title="Poroka ne obstaja" text="Preveri, ali si skeniral pravilno QR kodo ali odprl pravilno povezavo." />;
  if (!w) return <div className="min-h-screen grid place-items-center text-[#6C6560]" data-testid="guest-loading">Nalaganje …</div>;
  if (!w.can_view) {
    return w.status === "caka_placilo"
      ? <Notice title={w.title} text="Ta poročna galerija še ni aktivirana. Prosimo, poskusi znova kasneje." />
      : <Notice title={w.title} text="Obdobje hrambe te galerije je poteklo." />;
  }

  const total = w.stats.photos + w.stats.videos;
  const isToday = w.wedding_date === new Date().toISOString().slice(0, 10);
  const goGallery = () => { setTab("gallery"); setTimeout(() => document.getElementById("galerija")?.scrollIntoView({ behavior: "smooth" }), 50); };
  const share = async () => {
    const r = await shareLink(w.guest_url, w.title);
    if (r === "copied") toast.success("Povezava je kopirana.");
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2]" data-testid="guest-wedding-page">
      {w.is_demo && (
        <div className="bg-[#231F20] text-white text-xs text-center px-4 py-2" data-testid="demo-banner">
          <Info size={12} className="inline mr-1" /> Demo poroka z vzorčno vsebino. Datoteke, ki jih naložiš tukaj, se samodejno izbrišejo po 24 urah.
        </div>
      )}
      <header className="relative h-[64vh] min-h-[420px] overflow-hidden grain">
        <img src={fileUrl(w.cover_url) || DEFAULT_COVER} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/20 to-[#FAF7F2]" />
        <button onClick={share} className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/80 backdrop-blur grid place-items-center z-10" data-testid="guest-share-button" aria-label="Deli"><Share2 size={18} /></button>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="absolute bottom-0 inset-x-0 px-6 pb-6 text-center">
          <p className="eyebrow mb-3" data-testid="guest-wedding-date">{slDate(w.wedding_date)}</p>
          <h1 className="font-display text-5xl sm:text-6xl font-light tracking-tight leading-[1.05]" data-testid="guest-wedding-title">{w.title}</h1>
          <p className="mt-4 text-[#6C6560] max-w-md mx-auto font-display italic text-xl" data-testid="guest-welcome-message">“{w.welcome_message}”</p>
        </motion.div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 pb-24">
        <div className="max-w-md mx-auto grid gap-3 pt-4">
          {w.can_upload ? (
            <button onClick={() => setUploadOpen(true)} className="btn-dark h-16 text-lg flex items-center justify-center gap-3 shadow-[0_10px_30px_rgba(35,31,32,0.18)]" data-testid="guest-add-media-button"><Camera size={22} /> Dodaj fotografije in videe</button>
          ) : (
            <p className="text-center text-sm text-[#6C6560] bg-[#F3ECE1] rounded-2xl p-4" data-testid="guest-uploads-disabled">Nalaganje novih spominov trenutno ni omogočeno.</p>
          )}
          <button onClick={goGallery} className="btn-line h-14 flex items-center justify-center gap-3" data-testid="guest-view-gallery-button"><Heart size={20} className="text-[#C99A8E]" /> Poglej galerijo</button>
          {w.can_upload && w.settings.voice_enabled && (
            <button onClick={() => setVoiceOpen(true)} className="btn-line h-14 flex items-center justify-center gap-3" data-testid="guest-voice-button"><Mic size={20} className="text-[#B39264]" /> Pusti glasovno sporočilo</button>
          )}
        </div>

        <div className="text-center mt-10 space-y-2" data-testid="guest-stats">
          <p className="font-display text-2xl sm:text-3xl" data-testid="guest-memory-counter">{isToday ? "Danes smo skupaj ustvarili" : "Skupaj smo ustvarili"} {total} {total === 1 ? "spomin" : "spominov"}!</p>
          <p className="text-sm text-[#6C6560]">{photosLabel(w.stats.photos)} · {videosLabel(w.stats.videos)} · {guestsLabel(w.stats.guests)}</p>
        </div>

        <div id="galerija" className="mt-10 flex justify-start sm:justify-center gap-2 scroll-mt-4 overflow-x-auto no-scrollbar -mx-4 px-4">
          <button onClick={() => setTab("gallery")} className={`px-5 py-2.5 rounded-full text-sm flex items-center gap-2 ${tab === "gallery" ? "bg-[#231F20] text-white" : "bg-[#F3ECE1]"}`} data-testid="guest-tab-gallery"><Images size={16} /> Galerija</button>
          {w.settings.timeline_enabled && w.settings.timeline_public && w.settings.gallery_public && (
            <button onClick={() => setTab("timeline")} className={`px-5 py-2.5 rounded-full text-sm flex items-center gap-2 ${tab === "timeline" ? "bg-[#231F20] text-white" : "bg-[#F3ECE1]"}`} data-testid="guest-tab-timeline"><Clock size={16} /> Časovnica</button>
          )}
          {w.settings.voice_enabled && w.settings.voice_public && (
            <button onClick={() => setTab("voice")} className={`px-5 py-2.5 rounded-full text-sm flex items-center gap-2 ${tab === "voice" ? "bg-[#231F20] text-white" : "bg-[#F3ECE1]"}`} data-testid="guest-tab-voice"><Mic size={16} /> Glasovna knjiga</button>
          )}
          {w.settings.guestbook_enabled && (
            <button onClick={() => setTab("guestbook")} className={`px-5 py-2.5 rounded-full text-sm flex items-center gap-2 ${tab === "guestbook" ? "bg-[#231F20] text-white" : "bg-[#F3ECE1]"}`} data-testid="guest-tab-guestbook"><BookHeart size={16} /> Knjiga gostov</button>
          )}
        </div>
        <div className="mt-6">
          {tab === "gallery" && <Gallery slug={slug} wedding={w} token={token} refreshKey={refresh} />}
          {tab === "timeline" && <GuestTimeline slug={slug} title={`Časovnica poroke ${w.partner1} & ${w.partner2}`} />}
          {tab === "voice" && <VoiceList slug={slug} title={`Glasovna knjiga ${w.partner1} & ${w.partner2}`} />}
          {tab === "guestbook" && <Guestbook slug={slug} canWrite={w.can_upload} />}
        </div>
      </main>

      {w.can_upload && (
        <button onClick={() => setUploadOpen(true)} className="fixed bottom-5 right-5 sm:hidden w-14 h-14 rounded-full btn-gold grid place-items-center shadow-lg z-40" data-testid="guest-fab-upload" aria-label="Dodaj"><Camera size={22} /></button>
      )}
      <UploadFlow open={uploadOpen} onClose={() => setUploadOpen(false)} slug={slug} wedding={w} token={token}
        onUploaded={() => { setRefresh((x) => x + 1); load(); }} onShowGallery={() => { setUploadOpen(false); goGallery(); }} />
      <VoiceRecorder open={voiceOpen} onClose={() => setVoiceOpen(false)} slug={slug} wedding={w} token={token} />
    </div>
  );
}
